import {CircularProgress, styled} from '@mui/material';
import {i18n} from 'i18next';
import moment from 'moment';
import {FC, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {Column} from 'react-table';
import {
  Dialog,
  Table,
  TableCellMenu,
  TablePagination,
  TableSort,
  ToastSeverity,
  useToast
} from 'thermia-react-container';
import DeviceIcon from '../../../components/assets/icons/deviceIcon';
import DateRangeFilter, {
  DateRange,
  GetDateRangeFromDateTimeOffsets
} from '../../../components/table/dateRangeFilter';
import TableSkeleton from '../../../components/table/skeleton';
import {RegistrationsContext} from '../../../context/contextProvider';
import generatePdf from '../../../pdf/generatePdf';
import { RegistrationMetaData} from '../../../types/registrationTypes';
import { extractDevicesArticleNameFromMetaData, extractDeviceSerialNumberFromMetaData } from '../../../validation/extractDeviceSerialNumber';
import ReopenForm from './reopenForm';
import {useRegistrationService} from "../../../service/registrationService";
import {useCurrentPartner} from "../../registration/hooks/useCurrentPartner";

const StyledContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  height: '36px'
});

const StyledTableHead = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.tableRow.main,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4)
}));

type CompletedRegistrationsProps = {
  isLoadingResults: boolean;
};

export interface RegistrationCompletedForTable {
  id: string;
  deviceSerialNumbers: string;
  deviceArticleName: string;
  ownerName: string;
  updatedAt: string;
  installationDate: string;
  location: string;
}

const mapCompletedRegistrationsMetaData = (completedRegistrationsMetaData: RegistrationMetaData[], t: i18n['t']): RegistrationCompletedForTable[] => {
  return completedRegistrationsMetaData.map((reg) => ({
    id: reg.id,
    ownerName: reg.endCustomerFullName ?? 'no-owner',
    updatedAt: reg.updated ?? '',
    installationDate: reg.installationDate ? moment(reg.installationDate).format('YYYY-MM-DD') : '',
    deviceSerialNumbers: extractDeviceSerialNumberFromMetaData(reg),
    deviceArticleName: extractDevicesArticleNameFromMetaData(reg, t, true),
    location: reg.installationAddress ?? 'no-address',
  }));
};
const CompletedRegistrationsTable: FC<CompletedRegistrationsProps> = ({ isLoadingResults }) => {
  const { getRegistration } = useRegistrationService();
  const [open, setOpen] = useState(false);
  const [rowMenuOpenId, setRowMenuOpenId] = useState<string>();
  const { config, registrations } = useContext(RegistrationsContext);
  const { installationConfig } = config;
  const { setActiveRegistration, activeRegistration, completedRegistrationsMetaData, hasLoadedAnyCompletedRegistrationsMetaData } = registrations;

  const partner = useCurrentPartner();

  const { t } = useTranslation('registrations');
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();

  const [dateRangeFilter, setDateRangeFilter] = useState<DateRange>({startDate: moment('2001-01-01'), endDate: moment()});

  // Get DateRange from completedRegistrationsMetaData
  const updatedAtRange = useMemo(() => {
    const dateRange = GetDateRangeFromDateTimeOffsets(
      completedRegistrationsMetaData.map(r => r.updated).filter(r => r !== undefined) as string[]
    );
    setDateRangeFilter(dateRange);
    return dateRange;
  }, [completedRegistrationsMetaData]);
  
  const flattenedCompletedRegistrations = useMemo(() => {
    return mapCompletedRegistrationsMetaData(completedRegistrationsMetaData, t);
  }, [completedRegistrationsMetaData, t]);

  const handleViewRegistration = useCallback(
    async (id: string) => {
      const registration = await getRegistration(id);
      setActiveRegistration(registration);
      navigate(`/completed-registrations/${id}/customer`, { state: { registrationIsCompleted: true } });
    },
    [getRegistration, navigate, setActiveRegistration]
  );

  const handleEditRegistration = useCallback(
    async (id: string) => {
      const registration = await getRegistration(id);
      setActiveRegistration(registration);
      setOpen(true);
    },
    [getRegistration, setActiveRegistration]
  );
  
  const handleCreatePdf = useCallback(
    async (id: string) => {
      try {
        const registration = await getRegistration(id);
        if (registration !== undefined) {
          generatePdf({ registration, partner, t, installationConfig });
        }
      } catch (e) {
        toast.addToast(t('registrations.general.createPDFError'), ToastSeverity.ERROR);
      }
    },
    [installationConfig, partner, t, toast, getRegistration]
  );

  const fetchDataFunction = (
    pagination: TablePagination | null,
    search: string | null,
    sort: TableSort | null
  ): Promise<{ data: RegistrationCompletedForTable[]; totalCount: number }> => {
    return new Promise((resolve) => {
      let filteredRegistrations = filterRegistrations(search?.trim() ?? '', flattenedCompletedRegistrations);
      if (sort) {
        filteredRegistrations = filteredRegistrations.sort((a, b) => {
          const key = sort.column as keyof RegistrationCompletedForTable;
          return (a[key] > b[key] ? 1 : -1) * (sort.direction === 'asc' ? 1 : -1);
        });
      }
      const filteredData = filteredRegistrations.slice(pagination!.skip, pagination!.skip + pagination!.take);
      const totalCount = filteredRegistrations.length;
      resolve({ data: filteredData, totalCount });
    });
  };

  // Table methods, will be set by the Table component
  const [tableMethods] = useState({
    refreshData: () => Promise.resolve(), // function will be set by the table
    setSearch: (search: string) => {} // will be set by the Table
  });

  // On mount, check for 'search' param in query string and set Table search if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam && searchParam.trim() !== '') {
      if (typeof tableMethods.setSearch === 'function') {
        tableMethods.setSearch(searchParam);
      }
    }
  }, [location.search, tableMethods]);

  const refreshTableData = useCallback(async () => {
    await tableMethods.refreshData();
  }, [tableMethods]);
  
  useEffect(() => {
    refreshTableData();
  }, [completedRegistrationsMetaData, dateRangeFilter, refreshTableData]);

  const filterRegistrations = (search: string, data: RegistrationCompletedForTable[]) => {
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return data.filter((row) => {
      // Date filtering
      let dateMatch = true;
      dateMatch = dateMatch && moment(row.updatedAt).isSameOrAfter(dateRangeFilter.startDate, 'day');
      dateMatch = dateMatch && moment(row.updatedAt).isSameOrBefore(dateRangeFilter.endDate, 'day');
      // Multi-word search: all words must match at least one field
      const rowFields = [
        row.deviceSerialNumbers,
        row.deviceArticleName,
        row.ownerName,
        row.updatedAt,
        row.location
      ].map(f => f.toLowerCase());
      const searchMatch = searchWords.every(word =>
        rowFields.some(field => field.includes(word))
      );
      return dateMatch && searchMatch;
    });
  };

  const columns: Column<RegistrationCompletedForTable>[] = useMemo(() => {
    return [
      {
        accessor: 'deviceSerialNumbers',
        Header: t('registrations.general.serialNumber') ?? 'SerialNumber',
        sortable: true,
        Cell: (prop) => (
          <>
            {prop.row.original.deviceSerialNumbers.split(',').map((serialNumber, index) => (
              <StyledContainer key={`${prop.row.id}-${serialNumber}-${index}`}>
                <DeviceIcon />
                <span>{serialNumber}</span>
              </StyledContainer>
            ))}
          </>
        )
      },
      {
        accessor: 'deviceArticleName',
        Header: t('registrations.general.articleName') ?? 'ArticleName',
        sortable: true,
        Cell: (prop) => (
          <>
            {prop.row.original.deviceArticleName.split(',').map((articleName, index) => (
              <StyledContainer key={`${prop.row.id}-${articleName}-${index}`}>
                <span>{articleName}</span>
              </StyledContainer>
            ))}
          </>
        )
      },
      {
        accessor: 'installationDate',
        sortable: true,
        Header: t('registrations.general.installationDate') ?? 'installationDate'
      },
      {
        accessor: 'location',
        sortable: true,
        Header: t('registrations.general.installationAddress') ?? 'InstallationAddress'
      },
      {
        accessor: 'ownerName',
        sortable: true,
        Header: t('registrations.general.owner') ?? 'Owner'
      },
      {
        accessor: 'updatedAt',
        sortable: true,
        Header: t('registrations.general.registered') ?? 'registered',
        Cell: (prop) => <>{prop.row.original.updatedAt !== '' ? moment(prop.row.original.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}</>
      },
      {
        accessor: 'id',
        id: 'menu',
        Header: '',
        Cell: (prop) => {
          return (
            <TableCellMenu
              isOpen={prop.row.original.id === rowMenuOpenId}
              callBack={() => setRowMenuOpenId(prop.row.original.id)}
              menuItems={[
                {
                  onClick: () => handleViewRegistration(prop.row.original.id),
                  label: t('registrations.general.view')
                },
                {
                  onClick: () => handleEditRegistration(prop.row.original.id),
                  label: t('registrations.general.edit')
                },
                {
                  onClick: () => handleCreatePdf(prop.row.original.id),
                  label: t('registrations.general.createPDF')
                }
              ]}
            />
          );
        }
      }
    ];
  }, [handleCreatePdf, handleViewRegistration, handleEditRegistration, rowMenuOpenId, t]);

  return (
    <>
      <Dialog id={'reopen-completed-registration'} open={open}>
        <ReopenForm selectedRegistration={activeRegistration} handleCancel={() => setOpen(false)} />
      </Dialog>
      {isLoadingResults && !hasLoadedAnyCompletedRegistrationsMetaData ? (
        <TableSkeleton />
      ) : (
        <>
          <StyledTableHead>
            <DateRangeFilter 
              startDate={dateRangeFilter.startDate}
              endDate={dateRangeFilter.endDate}
              minDate={updatedAtRange.startDate}
              maxDate={updatedAtRange.endDate}
              onChange={setDateRangeFilter}
            />
          </StyledTableHead>
          <Table
            fetchData={fetchDataFunction}
            searchable
            columns={columns}
            pagination={true}
            sortable={true}
            defaultSortBy={{ column: 'installationDate', direction: 'desc' }}
            exposedMethods={tableMethods}
            emptyDataElement={isLoadingResults && completedRegistrationsMetaData.length === 0 ? <CircularProgress size={20} /> : undefined}
          />
        </>
      )}
    </>
  );
};

export default CompletedRegistrationsTable;
